import React, { useState, useEffect, useRef, useMemo } from "react";
import { ToastMessage, showToast } from "../../Shared/ToastMessage";
import axios from "axios";
import { localserver } from "../../../api/common";
import EventInvoice from "./invoice/invoice-web";
import { useUser } from "../../AdminUsers/UserContext";
import { FaSpinner } from "react-icons/fa";
import { RefundStatusUpdate,DownloadBadge,DownloadCertificate } from "../../../../src/api/Webservices";
import { RiRefund2Fill } from "react-icons/ri";
import { MdCancel } from "react-icons/md";

const PreviewAttendeeOrderModal = ({
  show,
  onHide,
  attendee = {},
  onMoreAction,
  cancelOrder,
  handleActionStatus,
}) => {
  const [isRefunding, setIsRefunding] = useState(false);
  const [showProfile, setShowProfile] = useState(true);
  const [showDetails, setShowDetails] = useState(true);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("activity");
  const [noteName, setNoteName] = useState("");
  const [noteText, setNoteText] = useState("");
  const [eventDetails, setEventDetails] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [getEventStatus, setGetEventStatus] = useState(false);
  const [triggerDownload, setTriggerDownload] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const invoiceRef = useRef(null);

  // Reset states when modal closes to prevent stale data
  useEffect(() => {
    if (!show) {
      setEventDetails([]);
      setAllTickets([]);
      setTriggerDownload(false);
      setIsDownloading(false);
      setGetEventStatus(false);
      setShowProfile(true);
      setShowDetails(true);
      setShowOrderSummary(false);
      setShowAllTickets(false);
      setShowPaymentDetails(false);
      setActiveTab("activity");
      setNoteName("");
      setNoteText("");
    }
  }, [show]);

  const handleRefundAttendeepayment = async (data) => {
    setIsRefunding(true);
    const payload = {
      event_id: data.event_id,
      attendee_payment_id: data.attendee_payment_id,
      refund_status: "yes",
    };

    try {
      const res = await RefundStatusUpdate(payload);

      showToast("Refund processed successfully", "success");

      await getEventDetailsByUser();
    } catch (err) {
      console.error(err);
      showToast("Failed to process refund", "error");
    } finally {
      setIsRefunding(false);
    }
  };

  const getEventDetailsByUser = async () => {
    try {
      setGetEventStatus(true);
      const response = await axios.post(
        `${localserver}/getOrderDetailsByID`,
        {
          event_id: attendee.event_id,
          attendee_payment_id: attendee.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.data.success) {
        setEventDetails(response.data || []);
      } else {
        console.error("Error: Failed to fetch user data");
        showToast("Failed to fetch events", "error");
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      showToast("An error occurred while fetching events", "error");
    } finally {
      setGetEventStatus(false);
    }
  };

  useEffect(() => {
    if (show && attendee.id && attendee.event_id) {
      getEventDetailsByUser();
    } else if (show && attendee.isGuest) {
      setEventDetails({
        data: [
          {
            form_data: JSON.stringify([
              { custom_fields: attendee.custom_fields || [] },
            ]),
            reg_code: attendee.reg_code || attendee.registrationId || "N/A",
            status: attendee.status || "confirmed",
            created_at: attendee.registeredOn || "",
            email: attendee.email || "N/A",
            first_name: attendee.first_name || "",
            last_name: attendee.last_name || "",
            attendee_id: attendee.id || null,
            ticket_id: attendee.ticketType || "",
            ticket_name: attendee.ticketType || "Accompanying Person",
          },
        ],
        tickets: [
          {
            id: attendee.ticketType || "",
            ticket_name: attendee.ticketType || "Accompanying Person",
          },
        ],
        payments: [{ id: attendee.orderId || "N/A" }],
        attendees_payments: [
          {
            id: attendee.orderId || "N/A",
            buyer_details: attendee.buyer_details || "",
          },
        ],
      });
    }
  }, [show, attendee]);

  useEffect(() => {
    if (!eventDetails.data) return;

    const attendeesLocal = [];
    eventDetails.data.forEach((att) => {
      const formFieldsLocal = parseFormData(att.form_data);
      const firstNameLocal =
        formFieldsLocal.find(
          (f) => f.name === "first_name" || f.label === "First Name"
        )?.value ||
        att.first_name ||
        "N/A";
      const lastNameLocal =
        formFieldsLocal.find(
          (f) => f.name === "last_name" || f.label === "Last Name"
        )?.value ||
        att.last_name ||
        "";
      const emailLocal =
        formFieldsLocal.find(
          (f) => f.name === "email_address" || f.label === "Email Address"
        )?.value ||
        att.email ||
        "N/A";

      let ticketDataLocal = {};
      if (att.ticket_data) {
        try {
          ticketDataLocal = JSON.parse(att.ticket_data);
        } catch (e) {
          console.error("Error parsing ticket_data for name:", e);
        }
      }
      const ticketName =
        ticketDataLocal.title ||
        ticketDataLocal.displayTitle ||
        ticketDataLocal.ticket_name ||
        "N/A";

      const orderId =
        eventDetails.attendees_payments?.[0]?.id || attendee.orderId || "N/A";
      const registeredOn = formatDate(att.created_at);

      let addons = [];
      if (att.add_ons_data && att.add_ons_data !== "[]") {
        try {
          const parsedAddons = JSON.parse(att.add_ons_data);
          addons = parsedAddons
            .filter((a) => (a.quantity || 0) > 0)
            .map((a) => ({
              name: a.add_on_name || "N/A",
              quantity: a.quantity,
              price: a.price || 0,
            }));
        } catch (e) {
          console.error("Error parsing add_ons_data:", e);
        }
      }

      attendeesLocal.push({
        name: `${firstNameLocal} ${lastNameLocal}`.trim() || "Attendee",
        ticket_name: ticketName,
        reg_code: att.reg_code || "N/A",
        order_id: orderId,
        email: emailLocal,
        registered_on: registeredOn,
        addons,
      });

      // Add guests if any
      let parsedGuests = [];
      if (att.guest_data && att.guest_data !== "[]") {
        try {
          parsedGuests = JSON.parse(att.guest_data);
        } catch (e) {
          console.error("Error parsing guest_data:", e);
        }
      }
      parsedGuests.forEach((g) => {
        const gFirst =
          g.custom_fields?.find(
            (f) => f.name === "first_name" || f.label === "First Name"
          )?.value || "";
        const gLast =
          g.custom_fields?.find(
            (f) => f.name === "last_name" || f.label === "Last Name"
          )?.value || "";
        const gEmail =
          g.custom_fields?.find(
            (f) => f.name === "email_address" || f.label === "Email Address"
          )?.value || "N/A";
        attendeesLocal.push({
          name: `${gFirst} ${gLast}`.trim() || "Guest",
          ticket_name: "Accompanying Person",
          reg_code: g.reg_code || "N/A",
          order_id: orderId,
          email: gEmail,
          registered_on: registeredOn,
          addons: [],
        });
      });
    });

    setAllTickets(attendeesLocal);
  }, [eventDetails, attendee]);

  const isRefunded =
    eventDetails.attendees_payments?.[0]?.refund_status === "yes";

  const getTicketTaxBreakdown = (ticketData, price, sameState) => {
    let taxOptions = ticketData.tax_options || [];
    if (typeof taxOptions === "string") {
      try {
        taxOptions = JSON.parse(taxOptions);
      } catch (error) {
        console.error("Error parsing tax_options:", error);
        taxOptions = [];
      }
    }
    const taxType = ticketData.tax_type || "No Tax";
    if (taxType === "No Tax") {
      return { taxes: [], taxLabel: "", totalTax: 0, base: price };
    }
    let breakdown = [];
    let totalTax = 0;
    const isInterState = sameState === "no";
    let rate = 0;
    if (isInterState) {
      const igstOption = taxOptions.find((t) => t.tax_name === "IGST");
      if (igstOption) {
        rate = igstOption.tax_percentage;
        let taxAmount =
          taxType === "Excluding Tax"
            ? price * (rate / 100)
            : price * (rate / (100 + rate));
        totalTax = taxAmount;
        breakdown = [{ name: "IGST", percentage: rate, amount: taxAmount }];
      }
    } else {
      const cgstOption = taxOptions.find((t) => t.tax_name === "CGST");
      const sgstOption = taxOptions.find((t) => t.tax_name === "SGST");
      if (cgstOption && sgstOption) {
        rate = cgstOption.tax_percentage + sgstOption.tax_percentage;
        let taxAmount =
          taxType === "Excluding Tax"
            ? price * (rate / 100)
            : price * (rate / (100 + rate));
        totalTax = taxAmount;
        const cgstAmt = (cgstOption.tax_percentage / rate) * taxAmount;
        const sgstAmt = taxAmount - cgstAmt;
        breakdown = [
          {
            name: "CGST",
            percentage: cgstOption.tax_percentage,
            amount: cgstAmt,
          },
          {
            name: "SGST",
            percentage: sgstOption.tax_percentage,
            amount: sgstAmt,
          },
        ];
      }
    }
    // fallback
    if (breakdown.length === 0 && rate === 0) {
      rate = 18;
      let taxAmount =
        taxType === "Excluding Tax"
          ? price * (rate / 100)
          : price * (rate / (100 + rate));
      totalTax = taxAmount;
      breakdown = [{ name: "GST", percentage: rate, amount: taxAmount }];
    }
    const taxLabel =
      breakdown.length > 0
        ? `(${breakdown.map((t) => `${t.name} ${t.percentage}%`).join("+")})`
        : "";
    const base = taxType === "Excluding Tax" ? price : price - totalTax;
    return { taxes: breakdown, taxLabel, totalTax, base };
  };

  const payment = eventDetails.attendees_payments?.[0] || {};
  const sameState = eventDetails?.state_is_same || "yes";

  const {
    groupedTickets,
    groupedAddons,
    allTaxSummary,
    computedSubtotal,
    ticketSubtotal,
    addonSubtotal,
    ticketTaxSummary,
    addonTaxSummary,
  } = useMemo(() => {
    if (!eventDetails.data)
      return {
        groupedTickets: [],
        groupedAddons: [],
        allTaxSummary: [],
        computedSubtotal: 0,
        ticketSubtotal: 0,
        addonSubtotal: 0,
        ticketTaxSummary: [],
        addonTaxSummary: [],
      };
    const ticketGroups = {};
    const addonGroups = {};
    const globalTaxMap = {};
    const ticketTaxMap = {};
    const addonTaxMap = {};
    const addToTaxMap = (taxes, taxMap) => {
      taxes.forEach((t) => {
        const key = `${t.name}-${t.percentage}`;
        if (!taxMap[key]) {
          taxMap[key] = { name: t.name, percentage: t.percentage, amount: 0 };
        }
        taxMap[key].amount += t.amount;
      });
    };
    eventDetails.data.forEach((att) => {
      // tickets
      let ticketData = {};
      if (att.ticket_data) {
        try {
          ticketData = JSON.parse(att.ticket_data);
        } catch (e) {
          console.error("Error parsing ticket_data:", e);
        }
      } else {
        const t = eventDetails.tickets?.find((ti) => ti.id == att.ticket_id);
        ticketData = t || {};
      }
      const price = Number(ticketData.price) || 0;
      const { base, taxes: itemTaxes } = getTicketTaxBreakdown(
        ticketData,
        price,
        sameState
      );
      const ticketId = att.ticket_id;
      if (!ticketGroups[ticketId]) {
        ticketGroups[ticketId] = {
          ticket_name:
            ticketData.title ||
            ticketData.displayTitle ||
            ticketData.ticket_name ||
            "N/A",
          quantity: 0,
          total_base: 0,
          tax_type: ticketData.tax_type || "No Tax",
        };
      }
      ticketGroups[ticketId].quantity += 1;
      ticketGroups[ticketId].total_base += base;
      addToTaxMap(itemTaxes, ticketTaxMap);
      addToTaxMap(itemTaxes, globalTaxMap);
      // addons
      if (att.add_ons_data && att.add_ons_data !== "[]") {
        try {
          const addonsArray = JSON.parse(att.add_ons_data);
          addonsArray.forEach((addon) => {
            const addonId = addon.id;
            const addonPrice = Number(addon.price) || 0;
            const addonQty = Number(addon.quantity) || 0;
            if (addonQty === 0) return;
            const { base: addonBase, taxes: itemAddonTaxes } =
              getTicketTaxBreakdown(addon, addonPrice, sameState);
            const totalAddonBase = addonBase * addonQty;
            const addonTaxes = itemAddonTaxes.map((t) => ({
              ...t,
              amount: t.amount * addonQty,
            }));
            if (!addonGroups[addonId]) {
              addonGroups[addonId] = {
                add_on_name: addon.add_on_name || "N/A",
                quantity: 0,
                total_base: 0,
                unit_price: addonPrice,
                tax_type: addon.tax_type || "No Tax",
              };
            }
            addonGroups[addonId].quantity += addonQty;
            addonGroups[addonId].total_base += totalAddonBase;
            addToTaxMap(addonTaxes, addonTaxMap);
            addToTaxMap(addonTaxes, globalTaxMap);
          });
        } catch (e) {
          console.error("Error parsing add_ons_data:", e);
        }
      }
    });
    const gTickets = Object.values(ticketGroups);
    const gAddons = Object.values(addonGroups);
    const taxSum = Object.values(globalTaxMap);
    const tTaxSum = Object.values(ticketTaxMap);
    const aTaxSum = Object.values(addonTaxMap);
    const subTotal =
      gTickets.reduce((acc, g) => acc + g.total_base, 0) +
      gAddons.reduce((acc, g) => acc + g.total_base, 0);
    const tSubtotal = gTickets.reduce((acc, g) => acc + g.total_base, 0);
    const aSubtotal = gAddons.reduce((acc, g) => acc + g.total_base, 0);
    return {
      groupedTickets: gTickets,
      groupedAddons: gAddons,
      allTaxSummary: taxSum,
      computedSubtotal: subTotal,
      ticketSubtotal: tSubtotal,
      addonSubtotal: aSubtotal,
      ticketTaxSummary: tTaxSum,
      addonTaxSummary: aTaxSum,
    };
  }, [eventDetails, sameState]);

  const handleDownloadInvoice = () => {
    if (!eventDetails.data || getEventStatus || isDownloading) {
      console.error(
        "No event details available for invoice or still loading/downloading"
      );
      showToast(
        "Please wait for details to load or download to complete",
        "error"
      );
      return;
    }

    setIsDownloading(true);
    setTriggerDownload(true);
  };

  const handleDownloadCertificate = () => {
    if (!attendeeData?.certificate) {
      showToast("No certificate available", "error");
      return;
    }
const attendeeId =attendeeData.id
    const eventId =attendeeData.event_id
DownloadCertificate(attendeeId, eventId)
    .then((res) => {
      console.log(res)

      window.open(`${localserver}/downloadCertificate/${eventId}/${attendeeId}`, '_blank')
    })
    .catch((err) => {
      showToast("Failed to download badge", "error");
      console.error(err)
    })
    // console.log("Triggering certificate download...");
    // let htmlContent = attendeeData.certificate;
    
    // // Extract background URL from the HTML
    // const bgUrlMatch = htmlContent.match(/background:url\(['"]([^'"]+)['"]\)/);
    // const bgUrl = bgUrlMatch ? bgUrlMatch[1] : '';
    
    // // Preload the background image
    // const preloadImg = new Image();
    // preloadImg.src = bgUrl;
    
    // preloadImg.onload = () => {
    //   console.log('Background image loaded');
    //   const printWindow = window.open("", "_blank", "width=800,height=600");
    //   if (printWindow) {
    //     printWindow.document.write(`
    //       <!DOCTYPE html>
    //       <html>
    //       <head>
    //         <title>Certificate</title>
    //         <style>
    //           @media print {
    //             * {
    //               -webkit-print-color-adjust: exact !important;
    //               color-adjust: exact !important;
    //               print-color-adjust: exact !important;
    //             }
    //             body {
    //               margin: 0 !important;
    //               padding: 0 !important;
    //               -webkit-print-color-adjust: exact !important;
    //             }
    //             .certificate {
    //               background-image: url('${bgUrl}') !important;
    //               background-size: cover !important;
    //               background-position: center !important;
    //               background-repeat: no-repeat !important;
    //               -webkit-print-color-adjust: exact !important;
    //               width: 384px !important;
    //               height: 576px !important;
    //               margin: 0 auto !important;
    //               page-break-inside: avoid !important;
    //             }
    //             .text-field {
    //               -webkit-print-color-adjust: exact !important;
    //               color: inherit !important;
    //             }
    //           }
    //           body {
    //             font-family: Arial, sans-serif;
    //             margin: 0;
    //             padding: 20px;
    //             background: white;
    //           }
    //           .certificate {
    //             position: relative;
    //             width: 384px;
    //             height: 576px;
    //             margin: 0 auto;
    //             border: 1px solid #ccc;
    //             border-radius: 8px;
    //             overflow: hidden;
    //           }
    //           .qr {
    //             position: absolute;
    //             left: 100px;
    //             top: 100px;
    //             width: 120px;
    //             height: 120px;
    //             pointer-events: none;
    //           }
    //           .text-field {
    //             position: absolute;
    //             padding: 10px 14px;
    //             font-size: 16px;
    //             font-family: 'Arial', sans-serif;
    //             font-weight: 700;
    //             color: #000000;
    //             text-align: center;
    //             background: transparent;
    //             border: none;
    //             outline: none;
    //             pointer-events: none;
    //             white-space: nowrap;
    //             overflow: hidden;
    //             text-overflow: ellipsis;
    //             line-height: 1.2;
    //             width: 200px;
    //             height: 50px;
    //           }
    //         </style>
    //       </head>
    //       <body>
    //         ${htmlContent.replace('<body>', '').replace('</body></html>', '</body></html>')}
    //         <script>
    //           window.onload = function() {
    //             setTimeout(function() {
    //               window.print();
    //               // Close after print
    //               setTimeout(function() {
    //                 window.close();
    //               }, 1000);
    //             }, 500);
    //           };
    //         </script>
    //       </body>
    //       </html>
    //     `);
    //     printWindow.document.close();
    //   }
    // };
    
    // preloadImg.onerror = () => {
    //   console.error('Failed to load background image');
    //   // Fallback: proceed without preloading
    //   const printWindow = window.open("", "_blank", "width=800,height=600");
    //   if (printWindow) {
    //     printWindow.document.write(htmlContent);
    //     printWindow.document.close();
    //     setTimeout(() => {
    //       printWindow.print();
    //       setTimeout(() => printWindow.close(), 1000);
    //     }, 500);
    //   }
    // };
  };

  const handleDownloadBadge = () => {
    console.log(attendeeData,"attendeeData")
    if (!attendeeData?.badge) {
      showToast("No badge available", "error");
      return;
    }
    const attendeeId =attendeeData.id
    const eventId =attendeeData.event_id
DownloadBadge(attendeeId, eventId)
    .then((res) => {
      console.log(res)
      // Open the URL in a new tab
      window.open(`${localserver}/downloadBadgePDF/${eventId}/${attendeeId}`, '_blank')
    })
    .catch((err) => {
      showToast("Failed to download badge", "error");
      console.error(err)
    })
    // console.log("Triggering badge download...");
    // let htmlContent = attendeeData.badge;
    
    // // Extract background URL from the HTML
    // const bgUrlMatch = htmlContent.match(/background:url\(['"]([^'"]+)['"]\)/);
    // const bgUrl = bgUrlMatch ? bgUrlMatch[1] : '';
    
    // // Preload the background image
    // const preloadImg = new Image();
    // preloadImg.src = bgUrl;
    
    // preloadImg.onload = () => {
    //   console.log('Background image loaded');
    //   const printWindow = window.open("", "_blank", "width=800,height=600");
    //   if (printWindow) {
    //     printWindow.document.write(`
    //       <!DOCTYPE html>
    //       <html>
    //       <head>
    //         <title>Badge</title>
    //         <style>
    //           @media print {
    //             * {
    //               -webkit-print-color-adjust: exact !important;
    //               color-adjust: exact !important;
    //               print-color-adjust: exact !important;
    //             }
    //             body {
    //               margin: 0 !important;
    //               padding: 0 !important;
    //               -webkit-print-color-adjust: exact !important;
    //             }
    //             .badge {
    //               background-image: url('${bgUrl}') !important;
    //               background-size: cover !important;
    //               background-position: center !important;
    //               background-repeat: no-repeat !important;
    //               -webkit-print-color-adjust: exact !important;
    //               width: 384px !important;
    //               height: 576px !important;
    //               margin: 0 auto !important;
    //               page-break-inside: avoid !important;
    //             }
    //             .text-field {
    //               -webkit-print-color-adjust: exact !important;
    //               color: inherit !important;
    //             }
    //             .qr {
    //               -webkit-print-color-adjust: exact !important;
    //             }
    //           }
    //           body {
    //             font-family: Arial, sans-serif;
    //             margin: 0;
    //             padding: 20px;
    //             background: white;
    //           }
    //           .badge {
    //             position: relative;
    //             width: 384px;
    //             height: 576px;
    //             margin: 0 auto;
    //             border: 1px solid #ccc;
    //             border-radius: 8px;
    //             overflow: hidden;
    //           }
    //           .qr {
    //             position: absolute;
    //             left: 132px;
    //             top: 334px;
    //             width: 119px;
    //             height: 110px;
    //             pointer-events: none;
    //           }
    //           .text-field {
    //             position: absolute;
    //             padding: 10px 14px;
    //             font-size: 16px;
    //             font-family: 'Arial', sans-serif;
    //             font-weight: 400;
    //             color: #333333;
    //             text-align: center;
    //             background: transparent;
    //             border: none;
    //             outline: none;
    //             pointer-events: none;
    //             white-space: nowrap;
    //             overflow: hidden;
    //             text-overflow: ellipsis;
    //             line-height: 1.2;
    //             width: 200px;
    //             height: 50px;
    //           }
    //         </style>
    //       </head>
    //       <body>
    //         ${htmlContent.replace('<body>', '').replace('</body></html>', '</body></html>')}
    //         <script>
    //           window.onload = function() {
    //             setTimeout(function() {
    //               window.print();
    //               // Close after print
    //               setTimeout(function() {
    //                 window.close();
    //               }, 1000);
    //             }, 500);
    //           };
    //         </script>
    //       </body>
    //       </html>
    //     `);
    //     printWindow.document.close();
    //   }
    // };
    
    // preloadImg.onerror = () => {
    //   console.error('Failed to load background image');
    //   // Fallback: proceed without preloading
    //   const printWindow = window.open("", "_blank", "width=800,height=600");
    //   if (printWindow) {
    //     printWindow.document.write(htmlContent);
    //     printWindow.document.close();
    //     setTimeout(() => {
    //       printWindow.print();
    //       setTimeout(() => printWindow.close(), 1000);
    //     }, 500);
    //   }
    // };
  };

  // Add small delay to ensure off-screen render completes before capture
  useEffect(() => {
    if (triggerDownload && invoiceRef.current) {
      const timer = setTimeout(() => {
        invoiceRef.current.handleDownloadPDF();
        setTriggerDownload(false);
        // Reset downloading after a delay to allow multiple clicks with cooldown
        setTimeout(() => {
          setIsDownloading(false);
        }, 2000); // 2 second cooldown
      }, 100); // 100ms delay for render safety
      return () => clearTimeout(timer);
    }
  }, [triggerDownload]);

  const handleNoteSubmit = (e) => {
    e.preventDefault();

    // Add your save logic here (e.g., API call)
  };

  const parseFormData = (formData) => {
    try {
      const parsed = JSON.parse(formData || "[]");
      return parsed[0]?.custom_fields || [];
    } catch (error) {
      console.error("Error parsing form_data:", error);
      return [];
    }
  };

  const parseBuyerDetails = (buyerDetails) => {
    try {
      const parsed = JSON.parse(buyerDetails || "{}");
      return parsed.custom_fields || [];
    } catch (error) {
      console.error("Error parsing buyer_details:", error);
      return [];
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Find the specific attendee data matching the selected attendee.id, or fallback to first
  const attendeeData =
    eventDetails.data?.find((d) => d.id === attendee.id) ||
    eventDetails.data?.[0] ||
    {};

  const buyerDetails = payment.buyer_details
    ? parseBuyerDetails(payment.buyer_details)
    : [];
  const formFields =
    buyerDetails.length > 0
      ? buyerDetails
      : parseFormData(attendeeData.form_data);
  const guestFormFields = parseFormData(attendeeData.guest_data) || [];
  const firstName =
    formFields.find((f) => f.name === "first_name" || f.label === "First Name")
      ?.value ||
    attendeeData.first_name ||
    "N/A";
  const lastName =
    formFields.find((f) => f.name === "last_name" || f.label === "Last Name")
      ?.value ||
    attendeeData.last_name ||
    "";
  const attendeeEmail =
    formFields.find(
      (f) => f.name === "email_address" || f.label === "Email Address"
    )?.value ||
    attendeeData.email ||
    "N/A";
  const ticket =
    eventDetails.tickets?.find((t) => t.id === attendeeData.ticket_id) ||
    eventDetails.tickets?.[0] ||
    {};

  let ticketData = attendeeData.ticket_data
    ? JSON.parse(attendeeData.ticket_data)
    : ticket;
  if (typeof ticketData.tax_options === "string") {
    try {
      ticketData.tax_options = JSON.parse(ticketData.tax_options);
    } catch (error) {
      console.error("Error parsing tax_options in ticketData:", error);
      ticketData.tax_options = [];
    }
  }
  const ticketPrice = Number.parseFloat(payment.total_ticket_amount || 0);
  const gstAmount = Number.parseFloat(payment.total_tax_amount || 0);
  const totalAmount = Number.parseFloat(payment.total_amount || 0);
  const dueAmount =
    totalAmount - Number.parseFloat(payment.payment_received || 0);
  const currency = payment.currency || "INR";
  const addonsAmount = Number.parseFloat(payment.total_addons_amount || 0);
  const subtotal = computedSubtotal;
  const couponAmount = Number.parseFloat(payment.coupon_applied_amount || 0);
  const couponPercentage = payment.coupon_applied_percentage || "0";
  const discountedSubtotal = subtotal - couponAmount;

  const attendeeDataForAction = {
    id: attendeeData.id || attendeeData.attendee_id || attendee.id || null,
    event_id:
      attendeeData.event_id ||
      attendee.event_id ||
      localStorage.getItem("eventId") ||
      "",
    form_data:
      attendeeData.form_data ||
      JSON.stringify([
        {
          custom_fields: attendee.isGuest
            ? attendee.custom_fields || []
            : formFields,
        },
      ]),
    guest_data: attendeeData.guest_data || "[]",
    ticket_id: attendeeData.ticket_id || ticket.id || attendee.ticketType || "",
    ticket_name:
      ticket.ticket_name ||
      attendeeData.ticket_name ||
      attendee.ticketType ||
      "N/A",
    reg_code: attendeeData.reg_code || attendee.registrationId || "N/A",
    status: attendeeData.status || attendee.status || "confirmed",
    created_at: attendeeData.created_at || attendee.registeredOn || "",
    email: attendeeEmail,
    first_name: firstName,
    last_name: lastName,
    nameOfBuyer: `${firstName} ${lastName}`.trim() || "Unknown",
    custom_fields: attendee.isGuest ? attendee.custom_fields || [] : formFields,
    isGuest: attendee.isGuest || attendeeData.is_guest || false,
    add_ons_data: attendeeData.add_ons_data || "[]",
    buyer_details: payment.buyer_details || "",
    attendee_payment_id: payment.id,
    ticket_data: attendeeData.ticket_data,
  };

  const isConfirmedStatus =
    attendeeDataForAction.status === "confirmed" || false;

  return (
    <>
      <ToastMessage />
      {show && (
        <div
          className={`extraspace_search_divattendies_view add_attendites_model member_master_sidebar_scrool search_div open`}
        >
          <div className="card mb-3">
            <div className="member_edit_pop_flex">
              <h3>Order Details - #{payment.id || "N/A"}</h3>
              <button
                className="search_toggle btn btn-danger"
                onClick={() => onHide()}
              >
                <i className="fa fa-arrow-left" /> Back
              </button>
            </div>
          </div>
          {getEventStatus ? (
            <div className="text-center p-5">
              <i className="fa fa-spinner fa-spin fa-3x"></i>
              <p className="mt-3">Loading event details...</p>
            </div>
          ) : (
            <div className="card">
              <div className="body">
                <div className="top-section">
                  <div className="inner_section_top">
                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showProfile ? "active" : ""
                      }`}
                      onClick={() => setShowProfile(!showProfile)}
                    >
                      {showProfile ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      Attendee Info
                    </button>

                    {showProfile && (
                      <div className="profile-card">
                        <div className="profile-header">
                          <div className="avathor_flex_div">
                            <div>
                              <div className="avatar">
                                <i className="fa fa-building-o"></i>
                              </div>
                              <div className="profile-info">
                                <h3>{`${firstName} ${lastName}`.trim()}</h3>
                                <div className="registration">
                                  <div className="register_id_name">
                                    Registration ID:{" "}
                                    <strong className="m-b-0">
                                      {attendeeData.reg_code || "N/A"}
                                    </strong>
                                  </div>
                                  <span className="status-confirmed">
                                    ●{" "}
                                    {attendeeData.status
                                      ?.charAt(0)
                                      .toUpperCase() +
                                      attendeeData.status?.slice(1) ||
                                      "Confirmed"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="but_button_flex">
                              {/* <button
                                className="btn btn-outline-primary"
                                onClick={() =>
                                  onMoreAction(attendeeDataForAction, "edit")
                                }
                              >
                                <i className="fa fa-edit me-1" /> Edit
                              </button> */}
                              {/* <button
                                className="btn btn-outline-primary"
                                onClick={() =>
                                  onMoreAction(attendeeDataForAction, "resend")
                                }
                              >
                                <i className="icon-envelope" /> Resend Success
                                Mail
                              </button> */}
                              {payment.payment_status === "Success" && (
                                <button
                                  className="btn btn-outline-primary"
                                  disabled={handleActionStatus}
                                  onClick={() => {
                                    onMoreAction(
                                      attendeeDataForAction,
                                      "resend"
                                    );
                                  }}
                                >
                                  {handleActionStatus ? (
                                    <>
                                      <FaSpinner
                                        className="fa-spin"
                                        style={{ fontSize: "14px" }}
                                      />{" "}
                                      Sending...
                                    </>
                                  ) : (
                                    "Resend Success Email"
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="details-card">
                            <h4>Details</h4>
                            <div className="details-grid">
                              {formFields.map((field, index) => (
                                <div key={index}>
                                  <label>{field?.label}</label>
                                  <p>{field?.value || "N/A"}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {payment.order_status === "confirmed" && (
                      <div className="order-card-section">
                        <button
                          className={`attendies_button_flex ${
                            showDetails ? "active" : ""
                          }`}
                          onClick={() => setShowDetails(!showDetails)}
                        >
                          {showDetails ? (
                            <i className="fa fa-angle-down" />
                          ) : (
                            <i className="fa fa-angle-up" />
                          )}{" "}
                          <div className="order_id_flex">
                            <div>Order ID: {payment.id || "N/A"}</div>
                          </div>
                        </button>

                        {showDetails && (
                          <div className="details_page_card mt-3">
                            <div className="order_head_flex">
                              <div>
                                <div className="sub_div">
                                  {allTickets.length} ticket
                                  {allTickets.length !== 1 ? "s" : ""}
                                </div>
                              </div>
                              <div>
                                <h5 className="mb-1">
                                  {payment.currency || "INR"}{" "}
                                  {totalAmount.toFixed(2)}
                                </h5>
                                <small className="text-success">
                                  ● {payment.payment_status || "Completed"}
                                </small>
                              </div>
                            </div>

                            <div>
                              <h4>Order Details</h4>
                              <div className="details-grid details-card">
                                <div>
                                  <label>Order Total</label>
                                  <p>
                                    {payment.currency || "INR"}{" "}
                                    {totalAmount.toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <label>Buyer</label>
                                  <p>{`${firstName} ${lastName}`.trim()}</p>
                                </div>
                                <div>
                                  <label>Amount Paid</label>
                                  <p>
                                    {payment.currency || "INR"}{" "}
                                    {Number.parseFloat(
                                      payment.payment_received || 0
                                    ).toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <label>Amount Due</label>
                                  <p>
                                    {payment.currency || "INR"}{" "}
                                    {dueAmount.toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <label>Order Initiated on</label>
                                  <p>{formatDate(payment.created_at)}</p>
                                </div>
                                <div>
                                  <label>Order Completed on</label>
                                  <p>{formatDate(payment.payment_date)}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showOrderSummary ? "active" : ""
                      }`}
                      onClick={() => setShowOrderSummary(!showOrderSummary)}
                    >
                      {showOrderSummary ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      Order Summary
                    </button>

                    {showOrderSummary && (
                      <div className="profile-card">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h4 className="mb-0">Order Summary</h4>
                        </div>
                        <div className="cancel_order_flex d-flex gap-2">
                            {payment.order_status !== "cancelled" &&
                              payment.order_status === "confirmed" && (
                                <button
                                  className="btn btn-danger"
                                  onClick={() =>
                                    onMoreAction(
                                      attendeeDataForAction,
                                      "cancel"
                                    )
                                  }
                                >
                                  Cancel Order
                                </button>
                              )}
                            {payment.order_status !== "cancelled" &&
                              isConfirmedStatus && (
                                <button
                                  className="btn btn-warning"
                                  onClick={() =>
                                    handleRefundAttendeepayment(
                                      attendeeDataForAction
                                    )
                                  }
                                  disabled={isRefunding || isRefunded}
                                >
                                  {isRefunding ? (
                                    <>
                                      <FaSpinner
                                        className="fa-spin me-1"
                                        style={{ fontSize: "14px" }}
                                      />
                                      Refunding...
                                    </>
                                  ) : isRefunded ? (
                                    "Refunded"
                                  ) : (
                                    "Refund"
                                  )}
                                </button>
                              )}
                            {payment.order_status === "confirmed" && (
                              <button
                                className="btn btn-outline-primary"
                                onClick={handleDownloadInvoice}
                                disabled={
                                  !eventDetails.data ||
                                  getEventStatus ||
                                  isDownloading
                                }
                              >
                                <i className="fa fa-download me-1" />{" "}
                                {isDownloading ? "Downloading..." : "Invoice"}
                              </button>
                            )}
                            {attendeeData.certificate && payment.order_status === "confirmed" && (
                              <button
                                className="btn btn-outline-primary"
                                onClick={handleDownloadCertificate}
                              >
                                <i className="fa fa-download me-1" /> Certificate
                              </button>
                            )}
                            {attendeeData.badge && payment.order_status === "confirmed" && (
                              <button
                                className="btn btn-outline-primary"
                                onClick={handleDownloadBadge}
                              >
                                <i className="fa fa-download me-1" /> Badge
                              </button>
                            )}
                          </div>
                        <div className="details-card">
                          <div className="order-summary-body">
                            <div className="d-flex justify-content-between mb-2">
                              <div className="title_div">Item</div>
                              <div className="title_div">Amount</div>
                            </div>

                            {/* Ticket Details */}
                            {groupedTickets.map((group, idx) => (
                              <div
                                key={`t-${idx}`}
                                className="d-flex justify-content-between text-muted mb-2"
                              >
                                <div className="sub_div">
                                  {group.quantity} × {group.ticket_name}
                                  {group.tax_type &&
                                    group.tax_type !== "No Tax" && (
                                      <div className="text-xs text-muted mt-1">
                                        Tax Type: {group.tax_type}
                                      </div>
                                    )}
                                </div>
                                <div className="sub_div">
                                  {currency} {group.total_base.toFixed(2)}
                                </div>
                              </div>
                            ))}

                            {/* Add-ons */}
                            {groupedAddons.map((group, idx) => (
                              <div
                                key={`a-${idx}`}
                                className="d-flex justify-content-between text-muted mb-2"
                              >
                                <div className="sub_div">
                                  {group.quantity} × {group.add_on_name} (
                                  {currency}
                                  {group.unit_price.toFixed(2)})
                                  {group.tax_type &&
                                    group.tax_type !== "No Tax" && (
                                      <div className="text-xs text-muted mt-1">
                                        Tax Type: {group.tax_type}
                                      </div>
                                    )}
                                </div>
                                <div className="sub_div">
                                  {currency} {group.total_base.toFixed(2)}
                                </div>
                              </div>
                            ))}

                            <hr />

                            {/* Subtotals and Taxes */}
                            {ticketSubtotal > 0 && (
                              <div className="d-flex justify-content-between text-muted mb-1">
                                <div className="title_div">
                                  Subtotal Tickets
                                </div>
                                <div className="title_div">
                                  {currency} {ticketSubtotal.toFixed(2)}
                                </div>
                              </div>
                            )}
                            {ticketTaxSummary.map((tax, idx) => (
                              <div
                                key={`tt-${idx}`}
                                className="d-flex justify-content-between text-muted mb-1"
                              >
                                <div className="sub_div">
                                  {tax.name} @ {tax.percentage}%
                                </div>
                                <div className="sub_div">
                                  {currency} {tax.amount.toFixed(2)}
                                </div>
                              </div>
                            ))}
                            {addonSubtotal > 0 && (
                              <div className="d-flex justify-content-between text-muted mb-1">
                                <div className="title_div">Add-ons</div>
                                <div className="title_div">
                                  {currency} {addonSubtotal.toFixed(2)}
                                </div>
                              </div>
                            )}
                            {addonTaxSummary.map((tax, idx) => (
                              <div
                                key={`at-${idx}`}
                                className="d-flex justify-content-between text-muted mb-1"
                              >
                                <div className="sub_div">
                                  {tax.name} @ {tax.percentage}%
                                </div>
                                <div className="sub_div">
                                  {currency} {tax.amount.toFixed(2)}
                                </div>
                              </div>
                            ))}
                            <div className="d-flex justify-content-between text-muted mb-3">
                              <div className="sub_div font-weight-bold">
                                Total Tax
                              </div>
                              <div className="sub_div font-weight-bold">
                                {currency}{" "}
                                {allTaxSummary
                                  .reduce((acc, tax) => acc + tax.amount, 0)
                                  .toFixed(2)}
                              </div>
                            </div>

                            <hr />

                            {/* Discount (if applied) */}
                            {couponAmount > 0 && (
                              <div className="d-flex justify-content-between text-muted mb-1">
                                <div className="sub_div">
                                  Discount ({couponPercentage}%)
                                </div>
                                <div className="sub_div">
                                  -{currency} {couponAmount.toFixed(2)}
                                </div>
                              </div>
                            )}

                            {/* Subtotal after discount */}
                            {couponAmount > 0 && (
                              <div className="d-flex justify-content-between text-muted mb-1">
                                <div className="title_div">
                                  Subtotal after discount
                                </div>
                                <div className="title_div">
                                  {currency} {discountedSubtotal.toFixed(2)}
                                </div>
                              </div>
                            )}

                            {/* Order Total */}
                            <div className="d-flex justify-content-between fw-bold">
                              <div className="title_div">Order Total</div>
                              <div className="sub_div">
                                {payment.currency || "INR"}{" "}
                                {totalAmount.toFixed(2)}
                              </div>
                            </div>

                            {/* Payment Status */}
                            <div className="mt-3 pt-2 border-top">
                              <div className="d-flex justify-content-between text-muted mb-1">
                                <div className="sub_div">
                                  Amount
                                  {payment.order_status === "confirmed"
                                    ? " paid"
                                    : ""}
                                </div>
                                <div
                                  className={`sub_div ${
                                    payment.order_status === "confirmed"
                                      ? "text-success"
                                      : "text-danger"
                                  }`}
                                >
                                  {payment.currency || "INR"}{" "}
                                  {Number.parseFloat(
                                    payment.payment_received || 0
                                  ).toFixed(2)}
                                </div>
                              </div>
                              {dueAmount > 0 && (
                                <div className="d-flex justify-content-between text-muted">
                                  <div className="sub_div">Amount Due</div>
                                  <div className="sub_div text-danger">
                                    {payment.currency || "INR"}{" "}
                                    {dueAmount.toFixed(2)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showAllTickets ? "active" : ""
                      }`}
                      onClick={() => setShowAllTickets(!showAllTickets)}
                    >
                      {showAllTickets ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      All Tickets ({allTickets.length})
                    </button>

                    {showAllTickets &&
                      allTickets.map((item, index) => (
                        <div key={index} className="profile-card">
                          <h4>{item.name}</h4>
                          <div className="details-card p-0">
                            <div className="order-summary-body p-0 border rounded shadow-sm overflow-Hidden">
                              <div className="px-3 pt-3 pb-2">
                                <p className="mb-2 sub_div">
                                  {item.ticket_name}
                                </p>
                                {item.addons && item.addons.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-muted small mb-1">
                                      Add-ons:
                                    </p>
                                    {item.addons.map((ad, adIdx) => (
                                      <p key={adIdx} className="sub_div mb-0">
                                        {ad.name} × {ad.quantity}
                                      </p>
                                    ))}
                                  </div>
                                )}
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between flex-wrap text-muted pb-3">
                                  <div className="mb-2">
                                    <div className="title_div">
                                      Registration ID
                                    </div>
                                    <div className="sub_div">
                                      {item.reg_code}
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <div className="title_div">Order ID</div>
                                    <div className="sub_div">
                                      {item.order_id}
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <div className="title_div">Email ID</div>
                                    <div className="sub_div">{item.email}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showPaymentDetails ? "active" : ""
                      }`}
                      onClick={() => setShowPaymentDetails(!showPaymentDetails)}
                    >
                      {showPaymentDetails ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      Payment Info
                    </button>

                    {showPaymentDetails && (
                      <div className="profile-card">
                        <div className="payment-card">
                          <div
                            className={`payment-status ${
                              payment.payment_status === "Success"
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            {payment.payment_status === "Success" && (
                              <i className="fa fa-check-circle"></i>
                            )}
                            <span>
                              Payment {payment.payment_status || "pending"}
                            </span>
                          </div>
                          <div className="payment-grid">
                            <div className="payment-item">
                              <span>Amount Paid</span>
                              <span className="paid">
                                {payment.currency || "INR"}{" "}
                                {Number.parseFloat(
                                  payment.payment_received || 0
                                ).toFixed(2)}
                              </span>
                            </div>
                            <div className="payment-item">
                              <span>Payment Date</span>
                              <span>{formatDate(payment.payment_date)}</span>
                            </div>
                            <div className="payment-item">
                              <span>Method</span>
                              {Number.parseFloat(payment.payment_received) >
                              0 ? (
                                <span>
                                  {payment.payment_method === "Razorpay"
                                    ? "Online"
                                    : payment.payment_method}
                                </span>
                              ) : (
                                <span>{"FREE"}</span>
                              )}
                            </div>
                            <div className="payment-item">
                              <span>Gateway</span>
                              {Number.parseFloat(payment.payment_received) ? (
                                <span>
                                  {payment.payment_id ? "Razorpay" : "-"}
                                </span>
                              ) : (
                                <span>{"-"}</span>
                              )}
                            </div>
                            {payment.payment_id && (
                              <div className="payment-item full-width">
                                <span>Reference ID</span>
                                <span className="ref-id">
                                  {payment.payment_id}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="email-card">
                    <div className="activity-notes-wrapper">
                      <div className="tabs-toggle tab-buttons mb-4 pillsContainer">
                        <button
                          className={`pill ${
                            activeTab === "activity" ? "activePill" : ""
                          }`}
                          onClick={() => setActiveTab("activity")}
                        >
                          Activity Log
                        </button>
                        {/* <button
                          className={`pill ${activeTab === "notes" ? "activePill" : ""}`}
                          onClick={() => setActiveTab("notes")}
                        >
                          Notes <span className="note-count">0</span>
                        </button> */}
                      </div>

                      {activeTab === "activity" && (
                        <div className="activity-log">
                          <div key={payment.id}>
                            {/* Refunded */}
                            {payment.refund_status === "yes" &&
                              payment.refund_date && (
                                <div className="activity-item refunded">
                                  <div className="icon-circle warning">
                                    <RiRefund2Fill
                                      className="text-yellow-600"
                                      size={20}
                                    />
                                  </div>
                                  <div className="activity-content">
                                    <div className="activity-title">
                                      Refunded
                                    </div>
                                    <div className="activity-time">
                                      {formatDate(payment.refund_date)}
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* Cancelled */}
                            {payment.cancel_date && (
                              <div className="activity-item cancelled">
                                <div className="icon-circle danger">
                                  <MdCancel
                                    className="text-red-600"
                                    size={20}
                                  />
                                </div>
                                <div className="activity-content">
                                  <div className="activity-title">
                                    Cancelled
                                  </div>
                                  <div className="activity-time">
                                    {formatDate(payment.cancel_date)}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Order confirmed/completed */}
                            {payment.order_status === "confirmed" && (
                              <div className="activity-item completed">
                                <div className="icon-circle success">
                                  <i className="icon-check"></i>
                                </div>
                                <div className="activity-content">
                                  <div className="activity-title">
                                    {payment.order_status === "confirmed"
                                      ? "Order Confirmed"
                                      : "Order Completed"}
                                  </div>
                                  <div className="activity-time">
                                    {formatDate(payment.payment_date)}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Payment */}
                            <div className="activity-item info">
                              <div className="icon-circle info">
                                <i className="icon-info"></i>
                              </div>
                              <div className="activity-content">
                                <div className="activity-title">
                                  Payment {payment.payment_status}
                                </div>
                                <div className="activity-time">
                                  {formatDate(payment.payment_date)}
                                </div>
                              </div>
                            </div>

                            {/* Order initiation */}
                            <div className="activity-item info">
                              <div className="icon-circle info">
                                <i className="icon-info"></i>
                              </div>
                              <div className="activity-content">
                                <div className="activity-title">
                                  Order Initiated
                                </div>
                                <div className="activity-time">
                                  {formatDate(payment.created_at)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* {activeTab === "notes" && (
                        <form className="notes-form" onSubmit={handleNoteSubmit}>
                          <div className="form-group">
                            <label htmlFor="noteName">Name</label>
                            <input
                              type="text"
                              id="noteName"
                              value={noteName}
                              onChange={(e) => setNoteName(e.target.value)}
                              placeholder="Enter your name"
                              required
                            />
                          </div>
                          <div className="file_div_noted">
                            <div className="file_div_noted_inner pt-3 pb-3">
                              <i className="fa fa-file-text"></i>
                            </div>
                            <div className="text-center no_notexs_preview">
                              <p>
                                <strong>No notes added yet!</strong>
                              </p>
                              <p>Only admins can see these notes</p>
                            </div>
                          </div>
                          <div className="text-center">
                            <button type="submit" className="btn btn-primary">
                              Save Note
                            </button>
                          </div>
                        </form>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {triggerDownload && (
            <div style={{ position: "absolute", left: "-9999px" }}>
              <EventInvoice
                ref={invoiceRef}
                invoiceData={eventDetails}
                autoDownload={true}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PreviewAttendeeOrderModal;